import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  Typography,
  Divider,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const DashboardDetails = ({ selectedApi, uploadedApiKey, setCurrentStep }) => {
  const [language, setLanguage] = useState("javascript");
  const [copy, setCopy] = useState(false);

  // Code examples in different languages
  const apiCodeExamples = {
    javascript: `// JavaScript (using Fetch API)
fetch('${selectedApi?.dataApi}', {
  headers: { 'Authorization': 'Bearer ${uploadedApiKey}' }
})
  .then(response => response.json())
  .then(data => console.log(data))
  .catch(error => console.error('Error:', error));`,

    python: `# Python (using Requests library)
import requests

headers = {'Authorization': 'Bearer ${uploadedApiKey}'}
response = requests.get('${selectedApi?.dataApi}', headers=headers)
print(response.json())`,

    curl: `# Command line (using cURL)
curl -X GET '${selectedApi?.dataApi}' -H 'Authorization: Bearer ${uploadedApiKey}'`,

    ruby: `# Ruby (using Net::HTTP library)
require 'net/http'
require 'json'

url = URI('${selectedApi?.dataApi}')
http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Get.new(url)
request['Authorization'] = 'Bearer ${uploadedApiKey}'

response = http.request(request)
puts JSON.parse(response.body)`,

    php: `// PHP (using cURL)
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, '${selectedApi?.dataApi}');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, 1);
curl_setopt($ch, CURLOPT_HTTPHEADER, array('Authorization: Bearer ${uploadedApiKey}'));
$response = curl_exec($ch);
curl_close($ch);
$data = json_decode($response, true);
print_r($data);`,
  };

  // Function to copy code to clipboard
  const handleCopyCode = () => {
    navigator.clipboard.writeText(apiCodeExamples[language]);
    setCopy(true);
  };

  return (
    <div>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={() => setCurrentStep("list")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1 }}>
          API Documentation - {selectedApi?.title}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Typography variant="body1" sx={{ mb: 2 }}>
        The following example demonstrates how to fetch data from the API
        endpoint provided. You'll need to use your API key (provided below) for
        authorization. Choose your preferred programming language to view a
        sample code snippet.
      </Typography>

      <Tabs
        value={language}
        onChange={(e, newLang) => setLanguage(newLang)}
        textColor="primary"
        indicatorColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="JavaScript" value="javascript" />
        <Tab label="Python" value="python" />
        <Tab label="cURL" value="curl" />
        <Tab label="Ruby" value="ruby" />
        <Tab label="PHP" value="php" />
      </Tabs>

      <Box
        sx={{
          p: 2,
          backgroundColor: "#f5f5f5",
          borderRadius: 2,
          mt: 2,
          position: "relative",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Code Example ({language})
        </Typography>
        <Box
          component="pre"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.875rem",
            whiteSpace: "pre-wrap",
          }}
        >
          {apiCodeExamples[language]}
        </Box>
        <Button
          onClick={handleCopyCode}
          variant="outlined"
          startIcon={copy ? <CheckIcon /> : <ContentCopyIcon />}
          sx={{ position: "absolute", top: 10, right: 10 }}
        >
          {copy ? "copied" : "copy"}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Sample Response Data
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Below is an example of what a successful response from the API might
          look like. This sample is only for reference; the actual response data
          may vary.
        </Typography>
        <Box
          component="pre"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.875rem",
            whiteSpace: "pre-wrap",
            backgroundColor: "#f5f5f5",
            p: 2,
            borderRadius: 2,
          }}
        >
          {`{
  "id": "123",
  "title": "${selectedApi?.title}",
  "data": "Sample data response"
}`}
        </Box>
      </Box>
    </div>
  );
};

DashboardDetails.propTypes = {
  selectedApi: PropTypes.object.isRequired,
  uploadedApiKey: PropTypes.string.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
};

export default DashboardDetails;
